import axios from "axios";
import { computed, type ComputedRef } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { tags } from "@/api/common";
import { queryKeys } from "@/api/queryKeys";
import { useLocationSelector } from "@/composables/location";
import { endpoints } from "@/endpoints";
import useApiStore from "@/stores/api";

import type {
  VisitorCategory,
  VisitorCategoryResponse,
} from "@/api/visitorCategories";
import type { LocationGroup } from "@/api/locationGroups";

export const useGetVisitorCategories = (
  locationGroup?: ComputedRef<LocationGroup | undefined>
) => {
  const apiStore = useApiStore();
  const { selectedLocationGroupPk, isLocationsLoaded } = useLocationSelector();

  const filters = computed(() => ({
    locationGroupId: locationGroup?.value?.id ?? selectedLocationGroupPk.value,
  }));

  return useQuery({
    queryKey: queryKeys[tags.platform.visitorCategories].list(filters).queryKey,
    queryFn: async () => {
      const result = await axios.get<VisitorCategoryResponse>(
        apiStore.getEndpoint("platform", endpoints.visitorCategories.list),
        {
          params: {
            locationGroupPk: filters.value.locationGroupId,
          },
        }
      );
      return result.data;
    },
    select: (data) =>
      data.list.map<VisitorCategory>((category) => ({
        id: category.pk,
        numericId: Number(category.id),
        name: category.name,
        companyId: Number(category.company_id),
        order: Number(category.order),
        settings: {
          ...category.settings,
          agreement_append: Boolean(category.settings.agreement_append),
          agreement_hide: Boolean(category.settings.agreement_hide),
          catering_selection: Boolean(category.settings.catering_selection),
          employee_selection: Boolean(category.settings.employee_selection),
          enabled: Boolean(category.settings.enabled),
          visits_require_signature: Boolean(
            category.settings.visits_require_signature
          ),
          random_selection: Boolean(category.settings.random_selection),
          random_selection_chance: Number(
            category.settings.random_selection_chance
          ),
        },
        agreements: category.agreements,
        inductions: category.inductions,
      })),
    enabled: isLocationsLoaded,
    refetchOnWindowFocus: false,
  });
};
